/**
 * Merko Integrations — WebSocket job-progress projection.
 *
 * The integrations backend reuses the chat assistant's socket to project import
 * job progress:
 *
 *     {"type":"progress","source":"integration","job_id":"…",
 *      "app_id":"mailchimp","status":"running","processed":100,
 *      "total":null,"percent":null}
 *
 * Everything about how this is consumed follows from one line in the handoff:
 * *"The WebSocket projection is best-effort and can lag or be absent; always
 * reconcile with REST."* So nothing here is authoritative. A frame is a hint
 * that something moved — it updates a live counter and nudges the REST poll,
 * and it never decides a job's outcome on its own.
 *
 * Two further rules it keeps:
 *
 *   • Running progress is **indeterminate**: no total record count is known,
 *     and `percent` is 100 only for `completed`/`completed_with_errors`.
 *   • `source: "csv"` frames are a different producer with a different meaning
 *     and must never be reinterpreted here. The chat-assistant store routes by
 *     `source` before anything reaches this module.
 */

export interface IntegrationJobProgress {
    jobId: string;
    appId?: string;
    /** Raw wire status; `JobStatus` for a well-behaved server. */
    status?: string;
    /** Records seen so far. The only number that is meaningful while running. */
    processed?: number;
    /** Null while running — no total is known. */
    total?: number | null;
    /** Null while running; 100 only once the job has completed. */
    percent?: number | null;
    receivedAtMs: number;
}

type Listener = (progress: IntegrationJobProgress) => void;

const listeners = new Set<Listener>();
/** Latest frame per job, so a late subscriber is not left with nothing. */
const latest = new Map<string, IntegrationJobProgress>();

/** Cap the cache so a long session cannot grow it without bound. */
const MAX_TRACKED_JOBS = 50;

/**
 * Publish an integration `progress` frame.
 *
 * Called by the chat-assistant socket store, which owns the connection. A frame
 * without a `job_id` is dropped: `job_id` is the only thing that identifies
 * which job it belongs to, since these frames carry no channel or generation.
 */
export function publishJobProgress(frame: {
    job_id?: string;
    app_id?: string;
    status?: string;
    processed?: number;
    total?: number | null;
    percent?: number | null;
}): void {
    if (!frame.job_id) return;

    const progress: IntegrationJobProgress = {
        jobId: frame.job_id,
        appId: frame.app_id,
        status: frame.status,
        processed: typeof frame.processed === "number" ? frame.processed : undefined,
        total: frame.total ?? null,
        percent: frame.percent ?? null,
        receivedAtMs: Date.now(),
    };

    if (!latest.has(progress.jobId) && latest.size >= MAX_TRACKED_JOBS) {
        // Oldest insertion first, which is the Map's own iteration order.
        const oldest = latest.keys().next().value;
        if (oldest) latest.delete(oldest);
    }
    latest.set(progress.jobId, progress);

    listeners.forEach((listener) => {
        try {
            listener(progress);
        } catch {
            // A broken subscriber must not stop the others, and must never
            // propagate back into the socket's frame handler.
        }
    });
}

export function subscribeJobProgress(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** The most recent frame seen for a job, if any. */
export const lastJobProgress = (jobId: string): IntegrationJobProgress | undefined =>
    latest.get(jobId);

/** Test seam. */
export function __resetJobProgress(): void {
    listeners.clear();
    latest.clear();
}
